import { useCallback, useEffect, useState } from 'react';

// Local-only flag for "Save chat history". When off, conversations are still
// saved while active (so context, token consumption, and analytics work), but
// layouts/chat orphans them from the user on leave (detachConversation) so they
// vanish from the user's history while staying in the DB.
const STORAGE_KEY = 'sahib-save-history';

// Pure reader — returns the latest preference straight from localStorage.
// Defaults to true (save) when unset or unavailable. Used at "leave the chat"
// time so the decision reflects the current setting, not a stale closure.
export function getSaveHistoryPref(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return true;
    return raw !== 'false';
  } catch {
    return true;
  }
}

export function useSaveHistoryPref(): {
  saveHistory: boolean;
  setSaveHistory: (next: boolean) => void;
} {
  const [saveHistory, setSaveHistoryState] = useState<boolean>(getSaveHistoryPref);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, saveHistory ? 'true' : 'false');
    } catch {
      // localStorage may be unavailable (private mode, quota) — ignore.
    }
  }, [saveHistory]);

  const setSaveHistory = useCallback((next: boolean) => setSaveHistoryState(next), []);

  return { saveHistory, setSaveHistory };
}
